import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { OperatorId, AirtimeDenomination } from '../../types';
import { OperatorLogo, PaymentLogo, OPERATOR_CONFIGS } from '../../components/common/BrandLogo';
import {
  ArrowLeftRight,
  RefreshCw,
  CheckCircle2,
  Copy,
  Check,
  Sliders,
  Settings,
  X,
  Wallet as WalletIcon,
  PhoneCall,
  PlusCircle,
  XCircle,
  Sparkles,
} from 'lucide-react';

export const AdminBuybacksPage: React.FC = () => {
  const {
    buybacks,
    rates,
    airtimeDenominations,
    loading,
    actionLoading,
    pendingBuybacksCount,
    refresh,
    settleBuyback,
    updateAirtimeRate,
    saveAirtimeDenomination,
    toggleAirtimeDenomination,
    deleteAirtimeDenomination,
    batchUpdateAirtimeRates,
  } = useAdmin();

  // Top Tab: 'selling_rates' (Buy Airtime) vs 'buyback_queue' (Sell Airtime / Buybacks)
  const [activeAirtimeTab, setActiveAirtimeTab] = useState<'selling_rates' | 'buyback_queue'>('selling_rates');

  // Airtime Selling Denominations state
  const [selectedDenomOp, setSelectedDenomOp] = useState<OperatorId | 'all'>('all');
  const [showAddDenomModal, setShowAddDenomModal] = useState(false);
  const [editingDenom, setEditingDenom] = useState<AirtimeDenomination | null>(null);
  const [denomOp, setDenomOp] = useState<OperatorId>('mpt');
  const [denomAmount, setDenomAmount] = useState('');
  const [denomSellingPrice, setDenomSellingPrice] = useState('');
  const [denomCostPrice, setDenomCostPrice] = useState('');

  // Batch % Update Modal state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchOp, setBatchOp] = useState<OperatorId>('mpt');
  const [batchSellingRate, setBatchSellingRate] = useState('95');
  const [batchCostRate, setBatchCostRate] = useState('93');
  const [batchSaving, setBatchSaving] = useState(false);

  // Buyback Queue state
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [settlingId, setSettlingId] = useState<string | null>(null);
  const [payoutTxRef, setPayoutTxRef] = useState('');
  const [copiedPhone, setCopiedPhone] = useState<string | null>(null);

  // Buyback Rate editing state
  const [isEditingRates, setIsEditingRates] = useState(false);
  const [rateInputs, setRateInputs] = useState<Record<OperatorId, string>>({
    mpt: '85',
    atom: '85',
    mytel: '86',
    u9: '85',
  });
  const [rateSaving, setRateSaving] = useState(false);

  // Sync buyback rate inputs when rates load
  useEffect(() => {
    if (rates && rates.length > 0) {
      const next: Record<OperatorId, string> = { ...rateInputs };
      for (const r of rates) {
        if (r.direction === 'buy' && r.operator_id) {
          next[r.operator_id] = r.rate_percentage.toString();
        }
      }
      setRateInputs(next);
    }
  }, [rates]);

  // Denominations Handlers
  const handleOpenAddDenom = (op?: OperatorId) => {
    setEditingDenom(null);
    setDenomOp(op || (selectedDenomOp !== 'all' ? selectedDenomOp : 'mpt'));
    setDenomAmount('');
    setDenomSellingPrice('');
    setDenomCostPrice('');
    setShowAddDenomModal(true);
  };

  const handleOpenEditDenom = (den: AirtimeDenomination) => {
    setEditingDenom(den);
    setDenomOp(den.operator_id);
    setDenomAmount(den.airtime_amount.toString());
    setDenomSellingPrice(den.selling_price.toString());
    setDenomCostPrice(den.cost_price ? den.cost_price.toString() : '');
    setShowAddDenomModal(true);
  };

  const handleSaveDenom = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseInt(denomAmount.replace(/\D/g, ''), 10);
    const sPrice = parseInt(denomSellingPrice.replace(/\D/g, ''), 10);
    const cPrice = parseInt(denomCostPrice.replace(/\D/g, '') || '0', 10);

    if (!amt || amt <= 0) return alert('ဘေလ်ပမာဏ မှန်ကန်စွာ ဖြည့်ပေးပါ');
    if (!sPrice || sPrice <= 0) return alert('ရောင်းစျေး မှန်ကန်စွာ ဖြည့်ပေးပါ');

    try {
      await saveAirtimeDenomination({
        id: editingDenom ? editingDenom.id : undefined,
        operator_id: denomOp,
        airtime_amount: amt,
        selling_price: sPrice,
        cost_price: isNaN(cPrice) ? 0 : cPrice,
        is_active: editingDenom ? editingDenom.is_active : 1,
      });
      setShowAddDenomModal(false);
      setEditingDenom(null);
    } catch (err: any) {
      alert(err.message || 'ဘေလ်ပမာဏ သိမ်းဆည်းမှု မအောင်မြင်ပါ');
    }
  };

  const handleDeleteDenom = async (id: string) => {
    if (!confirm('ဤ ဘေလ်ပမာဏကို ဖျက်ရန် သေချာပါသလား?')) return;
    try {
      await deleteAirtimeDenomination(id);
    } catch (err: any) {
      alert(err.message || 'ဖျက်၍ မရပါ');
    }
  };

  const handleOpenBatchModal = (op?: OperatorId) => {
    setBatchOp(op || (selectedDenomOp !== 'all' ? selectedDenomOp : 'mpt'));
    setBatchSellingRate('95');
    setBatchCostRate('93');
    setShowBatchModal(true);
  };

  const handleApplyBatchRates = async (e: React.FormEvent) => {
    e.preventDefault();
    const sRate = parseFloat(batchSellingRate);
    const cRate = parseFloat(batchCostRate);

    if (isNaN(sRate) || sRate <= 0 || sRate > 100) {
      return alert('ရောင်းစျေး ရာခိုင်နှုန်း (%) မှန်ကန်စွာ ဖြည့်ပေးပါ (ဥပမာ 95)');
    }

    setBatchSaving(true);
    try {
      await batchUpdateAirtimeRates(batchOp, sRate, isNaN(cRate) ? undefined : cRate);
      setShowBatchModal(false);
    } catch (err: any) {
      alert(err.message || 'နှုန်းထားများ အားလုံးပြင်ဆင်မှု မအောင်မြင်ပါ');
    } finally {
      setBatchSaving(false);
    }
  };

  // Buyback Handlers
  const handleCopyPhone = (phone: string, id: string) => {
    navigator.clipboard.writeText(phone);
    setCopiedPhone(id);
    setTimeout(() => setCopiedPhone(null), 1500);
  };

  const handleConfirmSettle = async () => {
    if (!settlingId) return;
    try {
      await settleBuyback(settlingId, payoutTxRef || `PAYOUT-${Date.now()}`);
      setSettlingId(null);
      setPayoutTxRef('');
    } catch (err: any) {
      alert(err.message || 'Settlement failed');
    }
  };

  const handleSaveAllBuybackRates = async (e: React.FormEvent) => {
    e.preventDefault();
    setRateSaving(true);
    try {
      const ops: OperatorId[] = ['mpt', 'atom', 'mytel', 'u9'];
      for (const op of ops) {
        const val = parseFloat(rateInputs[op]);
        if (!isNaN(val) && val > 0 && val <= 100) {
          await updateAirtimeRate(op, val);
        }
      }
      setIsEditingRates(false);
    } catch (err: any) {
      alert(err.message || 'Failed to update rates');
    } finally {
      setRateSaving(false);
    }
  };

  const filteredBuybacks = buybacks.filter((bb) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') {
      return bb.status === 'awaiting_transfer' || bb.status === 'under_review' || bb.status === 'receipt_verified';
    }
    if (statusFilter === 'completed') {
      return bb.status === 'settled';
    }
    return true;
  });

  const activeSettlingBuyback = settlingId ? buybacks.find((b) => b.id === settlingId) : null;

  return (
    <div className="space-y-5 pb-28">
      {/* Title & Refresh */}
      <div className="px-1">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base sm:text-lg font-bold text-plum-dark flex items-center space-x-2 min-w-0">
            {activeAirtimeTab === 'selling_rates' ? (
              <>
                <PhoneCall className="w-5 h-5 text-primary shrink-0" />
                <span className="truncate">ဘေလ်ရောင်းစျေး သတ်မှတ်ချက်များ</span>
              </>
            ) : (
              <>
                <ArrowLeftRight className="w-5 h-5 text-primary shrink-0" />
                <span className="truncate">ဘေလ်ပြန်ဝယ် စာရင်း & နှုန်းထားများ</span>
              </>
            )}
          </h2>

          <div className="flex items-center space-x-1.5 shrink-0">
            <button
              onClick={refresh}
              title="အသစ်ပြန်တင်မည်"
              disabled={loading}
              className="p-1.5 sm:p-2 rounded-xl bg-surface-card border border-plum-border text-plum-dark hover:text-primary transition shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary' : ''}`} />
            </button>

            {activeAirtimeTab === 'selling_rates' && (
              <>
                <button
                  onClick={() => handleOpenBatchModal()}
                  className="bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 font-semibold text-xs py-1.5 sm:py-2 px-2 sm:px-2.5 rounded-xl transition flex items-center space-x-1 shrink-0 active:scale-95"
                  title="% ဖြင့် အားလုံးပြင်မည်"
                >
                  <Sliders className="w-3.5 h-3.5 text-primary" />
                  <span className="hidden xs:inline">% ဖြင့်ပြင်မည်</span>
                </button>
                <button
                  onClick={() => handleOpenAddDenom()}
                  className="bg-primary hover:bg-primary-hover active:scale-[0.98] text-white font-semibold text-xs py-1.5 sm:py-2 px-2.5 sm:px-3 rounded-xl transition shadow-xs flex items-center space-x-1 shrink-0"
                >
                  <PlusCircle className="w-3.5 h-3.5 text-white" />
                  <span>အသစ်ထည့်မည်</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Top Navigation Tabs */}
      <div className="flex bg-surface-card p-1 rounded-2xl border border-plum-border shadow-xs">
        <button
          onClick={() => setActiveAirtimeTab('selling_rates')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center space-x-1.5 ${
            activeAirtimeTab === 'selling_rates'
              ? 'bg-primary text-white shadow-xs'
              : 'text-plum-muted hover:text-plum-dark'
          }`}
        >
          <PhoneCall className="w-4 h-4" />
          <span>ဘေလ်ရောင်းစျေး ({airtimeDenominations.length})</span>
        </button>

        <button
          onClick={() => setActiveAirtimeTab('buyback_queue')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center space-x-1.5 relative ${
            activeAirtimeTab === 'buyback_queue'
              ? 'bg-primary text-white shadow-xs'
              : 'text-plum-muted hover:text-plum-dark'
          }`}
        >
          <ArrowLeftRight className="w-4 h-4" />
          <span>ဘေလ်ပြန်ဝယ်</span>
          {pendingBuybacksCount > 0 && (
            <span className="ml-1 px-1.5 py-0.2 text-[9px] font-bold rounded-full bg-red-500 text-white shadow-xs">
              {pendingBuybacksCount}
            </span>
          )}
        </button>
      </div>

      {/* TAB 1: SELLING RATES & DENOMINATIONS */}
      {activeAirtimeTab === 'selling_rates' && (
        <div className="space-y-4">
          {/* Operator Filter Pills */}
          <div className="flex space-x-1.5 overflow-x-auto pb-1 scrollbar-none">
            {[
              { id: 'all', label: 'အားလုံး' },
              { id: 'mpt', label: 'MPT' },
              { id: 'atom', label: 'ATOM' },
              { id: 'u9', label: 'U9' },
              { id: 'mytel', label: 'MYTEL' },
            ].map((op) => (
              <button
                key={op.id}
                onClick={() => setSelectedDenomOp(op.id as any)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                  selectedDenomOp === op.id
                    ? 'bg-primary text-white shadow-xs'
                    : 'bg-surface-card border border-plum-border text-plum-muted hover:text-plum-dark'
                }`}
              >
                {op.label}{' '}
                <span className="opacity-70 text-[10px]">
                  (
                  {op.id === 'all'
                    ? airtimeDenominations.length
                    : airtimeDenominations.filter((d) => d.operator_id === op.id).length}
                  )
                </span>
              </button>
            ))}
          </div>

          {/* Business Rule / Fee Policy Callout */}
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-3.5 text-xs text-plum-muted space-y-1">
            <p className="font-semibold text-plum-dark flex items-center space-x-1.5">
              <Sparkles className="w-4 h-4 text-primary" />
              <span>ဖုန်းဘေလ်ရောင်းချမှု ဈေးနှုန်းသတ်မှတ်ချက် စည်းမျဉ်း</span>
            </p>
            <p className="leading-relaxed">
              Customer များသည် သင်သတ်မှတ်ထားသော ပမာဏများကိုသာ ရွေးချယ်ဝယ်ယူနိုင်ပါမည်။ 
              သတ်မှတ်ထားသော ရောင်းစျေး (Fixed MMK) အတိုင်းသာ ပေးချေရမည်ဖြစ်ပြီး Transfer Fee အပိုမကောက်ဘဲ အပြီးအစီးဖြစ်ပါသည်။
            </p>
          </div>

          {/* Denominations List */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {airtimeDenominations
              .filter((d) => selectedDenomOp === 'all' || d.operator_id === selectedDenomOp)
              .map((den) => {
                const isActive = den.is_active === 1;
                const margin = den.selling_price - (den.cost_price || 0);

                return (
                  <div
                    key={den.id}
                    className={`bg-surface-card rounded-2xl border p-4 shadow-xs space-y-3 transition ${
                      isActive ? 'border-plum-border' : 'border-red-500/30 bg-red-950/10 opacity-75'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <OperatorLogo id={den.operator_id} size="xs" className="rounded-md" />
                          <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md uppercase font-mono">
                            {den.operator_id}
                          </span>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                              isActive
                                ? 'bg-green-500/10 text-status-green border-green-500/30'
                                : 'bg-red-500/10 text-status-red border-red-500/30'
                            }`}
                          >
                            {isActive ? '🟢 ရောင်းချနေသည်' : '🔴 ရပ်နားထားသည်'}
                          </span>
                        </div>
                        <h4 className="font-extrabold text-plum-dark text-base mt-1">
                          {den.airtime_amount.toLocaleString()} Ks ဘေလ်
                        </h4>
                      </div>

                      <div className="text-right">
                        <span className="text-base font-bold text-primary">
                          {den.selling_price.toLocaleString()} Ks
                        </span>
                        <p className="text-[10px] text-plum-muted mt-0.5">
                          ရင်း: {(den.cost_price || 0).toLocaleString()} Ks (
                          <span className={margin >= 0 ? 'text-status-green font-semibold' : 'text-status-red font-semibold'}>
                            {margin >= 0 ? `+${margin.toLocaleString()}` : margin.toLocaleString()}
                          </span>)
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-2 border-t border-plum-border/60 flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => toggleAirtimeDenomination(den.id)}
                        className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold border transition touch-target flex items-center justify-center space-x-1.5 ${
                          isActive
                            ? 'bg-surface-page hover:bg-red-500/10 text-plum-dark hover:text-status-red border-plum-border hover:border-red-500/30'
                            : 'bg-green-500/10 hover:bg-green-500/20 text-status-green border-green-500/30'
                        }`}
                      >
                        {isActive ? (
                          <>
                            <XCircle className="w-3.5 h-3.5" />
                            <span>ရောင်းချမှု ပိတ်မည်</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>ပြန်ဖွင့်မည်</span>
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleOpenEditDenom(den)}
                        className="py-2 px-3 rounded-xl bg-surface-page hover:bg-surface-accent border border-plum-border text-primary text-xs font-bold transition flex items-center space-x-1 touch-target"
                        title="စျေးနှုန်း ပြင်ဆင်မည်"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        <span>ပြင်မည်</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteDenom(den.id)}
                        className="p-2 rounded-xl bg-surface-page hover:bg-red-500/10 border border-plum-border text-plum-muted hover:text-status-red text-xs font-bold transition flex items-center justify-center touch-target"
                        title="ဖျက်မည်"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>

          {airtimeDenominations.filter((d) => selectedDenomOp === 'all' || d.operator_id === selectedDenomOp).length === 0 && (
            <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
              သတ်မှတ်ထားသော ဘေလ်ပမာဏ မရှိသေးပါ။ "+ အသစ်ထည့်မည်" ဖြင့် စတင်ထည့်သွင်းနိုင်ပါသည်။
            </div>
          )}
        </div>
      )}

      {/* TAB 2: BUYBACK QUEUE & BUYBACK RATES */}
      {activeAirtimeTab === 'buyback_queue' && (
        <div className="space-y-4">
          {/* Airtime Buyback Rates Card (Admin Configuration) */}
          <div className="bg-surface-card rounded-2xl border border-plum-border p-4 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Sliders className="w-3.5 h-3.5" />
                </div>
                <h3 className="text-xs font-bold text-plum-dark">ဖုန်းဘေလ်ပြန်ဝယ်နှုန်း (%)</h3>
              </div>

              <button
                type="button"
                onClick={() => setIsEditingRates(true)}
                className="px-2.5 py-1 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold flex items-center space-x-1 transition active:scale-95"
              >
                <Settings className="w-3 h-3" />
                <span>နှုန်းထားပြင်မည်</span>
              </button>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {(['mpt', 'atom', 'mytel', 'u9'] as OperatorId[]).map((op) => {
                const currentSchedule = rates.find((r) => r.direction === 'buy' && r.operator_id === op);
                const ratePercent = currentSchedule?.rate_percentage ?? (op === 'mytel' ? 86 : 85);
                return (
                  <div
                    key={op}
                    className="bg-surface-page p-2.5 rounded-xl border border-plum-border/60 flex flex-col items-center justify-center space-y-1 text-center"
                  >
                    <OperatorLogo id={op} size="xs" className="rounded-md" />
                    <span className="text-[10px] text-plum-muted font-medium uppercase">{op}</span>
                    <span className="font-mono font-bold text-sm text-primary">{ratePercent}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Buybacks Filter Tabs */}
          <div className="flex space-x-1.5 overflow-x-auto pb-1 scrollbar-none">
            {[
              { id: 'all', label: 'အားလုံး', count: buybacks.length },
              {
                id: 'pending',
                label: 'ငွေထုတ်ပေးရန် ဆိုင်းငံ့',
                count: buybacks.filter(
                  (b) => b.status === 'awaiting_transfer' || b.status === 'under_review' || b.status === 'receipt_verified'
                ).length,
              },
              {
                id: 'completed',
                label: 'ရှင်းပြီး',
                count: buybacks.filter((b) => b.status === 'settled').length,
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id as any)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                  statusFilter === tab.id
                    ? 'bg-primary text-white shadow-xs'
                    : 'bg-surface-card border border-plum-border text-plum-muted hover:text-plum-dark'
                }`}
              >
                {tab.label} <span className="opacity-70 text-[10px]">({tab.count})</span>
              </button>
            ))}
          </div>

          {/* Buybacks List */}
          <div className="space-y-3">
            {filteredBuybacks.map((bb) => {
              const isPending = bb.status === 'awaiting_transfer' || bb.status === 'under_review' || bb.status === 'receipt_verified';
              const isSettled = bb.status === 'settled';

              return (
                <div
                  key={bb.id}
                  className={`bg-surface-card rounded-2xl border p-4 shadow-xs space-y-3 transition ${
                    isPending ? 'border-primary/40 shadow-sm' : 'border-plum-border'
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2.5">
                      <OperatorLogo id={bb.operator_id} size="md" className="rounded-xl shadow-xs" />
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-mono text-sm font-bold text-plum-dark">
                            {bb.sender_phone}
                          </span>
                          <button
                            onClick={() => handleCopyPhone(bb.sender_phone, bb.id)}
                            className="p-1 text-plum-muted hover:text-primary transition"
                            title="လွှဲမည့်ဖုန်းနံပါတ် ကူးယူရန်"
                          >
                            {copiedPhone === bb.id ? (
                              <Check className="w-3.5 h-3.5 text-status-green" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                        <p className="text-[11px] text-plum-muted">
                          တောင်းဆိုမှု အမှတ်: <span className="font-mono">{bb.id.slice(0, 10)}...</span>
                        </p>
                      </div>
                    </div>

                    <span
                      className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                        isSettled
                          ? 'bg-green-500/10 text-status-green border-green-500/30'
                          : 'bg-amber-500/10 text-status-amber border-amber-500/30'
                      }`}
                    >
                      {isSettled ? '✅ ရှင်းပြီး' : '⏳ ဆိုင်းငံ့'}
                    </span>
                  </div>

                  {/* Amount & Settlement Info */}
                  <div className="bg-surface-page p-2.5 rounded-xl border border-plum-border/60 flex items-center justify-between text-xs">
                    <div>
                      <span className="text-[11px] text-plum-muted">လွှဲပေးမည့် ဘေလ်ပမာဏ:</span>
                      <p className="font-mono font-bold text-plum-dark text-sm">
                        {bb.airtime_amount.toLocaleString()} Ks
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-[11px] text-plum-muted">ပြန်လည်ထုတ်ပေးမည့်ငွေ:</span>
                      <p className="font-mono font-bold text-primary text-sm">
                        {bb.payout_amount.toLocaleString()} Ks
                      </p>
                    </div>
                  </div>

                  {/* Settlement Method Badge & Time */}
                  <div className="text-[11px] text-plum-muted flex items-center justify-between">
                    <span className="flex items-center space-x-1.5">
                      <span>ထုတ်ပေးမည့် ပုံစံ:</span>
                      {bb.settlement_type === 'external_kpay' && (
                        <span className="inline-flex items-center space-x-1 font-bold text-blue-700 bg-blue-500/15 px-2 py-0.5 rounded-lg border border-blue-500/30">
                          <PaymentLogo method="kpay" size="xs" />
                          <span>KBZPay</span>
                        </span>
                      )}
                      {bb.settlement_type === 'external_wave' && (
                        <span className="inline-flex items-center space-x-1 font-bold text-amber-700 bg-amber-500/15 px-2 py-0.5 rounded-lg border border-amber-500/30">
                          <PaymentLogo method="wavepay" size="xs" />
                          <span>WavePay</span>
                        </span>
                      )}
                      {bb.settlement_type === 'wallet' && (
                        <span className="inline-flex items-center space-x-1 font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-lg border border-primary/20">
                          <WalletIcon className="w-3 h-3" />
                          <span>PluvP Wallet</span>
                        </span>
                      )}
                    </span>
                    <span>{new Date(bb.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>

                  {/* Customer Payout Destination with Quick Copy */}
                  {bb.payout_destination && (
                    <div className="bg-surface-page p-2.5 rounded-xl border border-plum-border/60 flex items-center justify-between text-xs">
                      <span className="text-[11px] text-plum-muted">
                        ငွေလွှဲပေးရမည့် {bb.settlement_type === 'external_wave' ? 'WavePay' : 'KBZPay'} နံပါတ်:
                      </span>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono font-bold text-plum-dark text-xs">{bb.payout_destination}</span>
                        <button
                          type="button"
                          onClick={() => handleCopyPhone(bb.payout_destination!, `payout-${bb.id}`)}
                          className="px-2 py-1 rounded bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold flex items-center space-x-1 transition active:scale-95"
                          title="ငွေလွှဲရမည့် နံပါတ် ကူးယူရန်"
                        >
                          {copiedPhone === `payout-${bb.id}` ? (
                            <>
                              <Check className="w-3 h-3 text-status-green" />
                              <span>ကူးပြီး</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              <span>ကူးမည်</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Settle Action */}
                  {isPending && (
                    <div className="pt-2 border-t border-plum-border/60 flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSettlingId(bb.id);
                        }}
                        disabled={actionLoading}
                        className="flex-1 bg-primary hover:bg-primary-hover active:scale-[0.98] text-white font-bold text-xs py-2.5 rounded-xl transition shadow-xs touch-target flex items-center justify-center space-x-1.5"
                      >
                        <CheckCircle2 className="w-4 h-4 text-white" />
                        <span>✅ ဘေလ်ရောက်ရှိပြီး ငွေထုတ်ပေးမည်</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {filteredBuybacks.length === 0 && (
              <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
                {statusFilter === 'all'
                  ? 'ဘေလ်ပြန်ဝယ်မှု ဆိုင်းငံ့စာရင်း မရှိပါ။'
                  : 'ဤအခြေအနေရှိ တောင်းဆိုမှု မရှိပါ။'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add / Edit Denomination Modal */}
      {showAddDenomModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-2 border-b border-plum-border/60">
              <h3 className="font-bold text-plum-dark text-base">
                {editingDenom ? 'ဘေလ်ပမာဏ ပြင်ဆင်ရန်' : 'ဘေလ်ပမာဏ အသစ်ထည့်မည်'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddDenomModal(false);
                  setEditingDenom(null);
                }}
                className="p-1 rounded-full text-plum-muted hover:text-plum-dark"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveDenom} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">Operator ရွေးပါ</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {(['mpt', 'atom', 'u9', 'mytel'] as OperatorId[]).map((op) => (
                    <button
                      key={op}
                      type="button"
                      onClick={() => setDenomOp(op)}
                      className={`py-2 text-xs font-bold rounded-xl border uppercase transition flex flex-col items-center space-y-1 ${
                        denomOp === op
                          ? 'border-primary bg-primary/10 text-primary shadow-xs'
                          : 'border-plum-border bg-surface-page text-plum-muted'
                      }`}
                    >
                      <OperatorLogo id={op} size="xs" className="rounded-md" />
                      <span>{op}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">
                  ဘေလ်ပမာဏ (Airtime Amount)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={denomAmount}
                  onChange={(e) => setDenomAmount(e.target.value)}
                  placeholder="ဥပမာ: 1000, 2000, 3000, 5000"
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono font-bold text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <p className="text-[10px] text-plum-muted mt-1">
                  Customer ထံသို့ လွှဲပေးရမည့် ဖုန်းဘေလ် ပမာဏ (Ks)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    ရောင်းစျေး (Fixed MMK)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={denomSellingPrice}
                    onChange={(e) => setDenomSellingPrice(e.target.value)}
                    placeholder="ဥပမာ: 950"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono font-bold text-primary focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <p className="text-[10px] text-plum-muted mt-1">
                    Customer ပေးချေရမည့်ငွေ (လွှဲခ မပါ)
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    ရင်းစျေး (Cost Price)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={denomCostPrice}
                    onChange={(e) => setDenomCostPrice(e.target.value)}
                    placeholder="ဥပမာ: 930"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <p className="text-[10px] text-plum-muted mt-1">
                    အမြတ်တွက်ချက်ရန် အရင်းငွေ
                  </p>
                </div>
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddDenomModal(false);
                    setEditingDenom(null);
                  }}
                  className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
                >
                  ပယ်ဖျက်မည်
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
                >
                  {actionLoading ? 'သိမ်းနေပါသည်...' : 'အတည်ပြုသိမ်းမည်'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch % Update Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-2 border-b border-plum-border/60">
              <h3 className="font-bold text-plum-dark text-base flex items-center space-x-1.5">
                <Sliders className="w-4 h-4 text-primary" />
                <span>% ဖြင့် ဈေးနှုန်းများ အားလုံးပြင်မည်</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowBatchModal(false)}
                className="p-1 rounded-full text-plum-muted hover:text-plum-dark"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-plum-muted leading-relaxed">
              ရွေးချယ်ထားသော အော်ပရေတာ၏ ဘေလ်ပမာဏများ အားလုံးကို ရာခိုင်နှုန်း (%) ဖြင့် အလိုအလျောက် တွက်ချက်ပြောင်းလဲပေးပါမည်:
            </p>

            <form onSubmit={handleApplyBatchRates} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">အော်ပရေတာ ရွေးပါ</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {(['mpt', 'atom', 'u9', 'mytel'] as OperatorId[]).map((op) => (
                    <button
                      key={op}
                      type="button"
                      onClick={() => setBatchOp(op)}
                      className={`py-2 text-xs font-bold rounded-xl border uppercase transition flex flex-col items-center space-y-1 ${
                        batchOp === op
                          ? 'border-primary bg-primary/10 text-primary shadow-xs'
                          : 'border-plum-border bg-surface-page text-plum-muted'
                      }`}
                    >
                      <OperatorLogo id={op} size="xs" className="rounded-md" />
                      <span>{op}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    ရောင်းစျေး (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      min="10"
                      max="100"
                      value={batchSellingRate}
                      onChange={(e) => setBatchSellingRate(e.target.value)}
                      placeholder="95"
                      className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono font-bold text-primary focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <span className="absolute right-3 top-2.5 text-xs font-bold text-plum-muted">%</span>
                  </div>
                  <p className="text-[10px] text-plum-muted mt-1">ဥပမာ: 95% (1000 = 950)</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    ရင်းစျေး (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      min="10"
                      max="100"
                      value={batchCostRate}
                      onChange={(e) => setBatchCostRate(e.target.value)}
                      placeholder="93"
                      className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-sm font-mono text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <span className="absolute right-3 top-2.5 text-xs font-bold text-plum-muted">%</span>
                  </div>
                  <p className="text-[10px] text-plum-muted mt-1">ဥပမာ: 93% (1000 = 930)</p>
                </div>
              </div>

              {/* Instant Calculation Preview */}
              <div className="bg-surface-page p-3 rounded-xl border border-plum-border space-y-1.5 text-xs">
                <span className="text-[11px] font-bold text-plum-dark block">တွက်ချက်မှု နမူနာ ပြသချက်:</span>
                {[1000, 3000, 5000, 10000].map((amt) => {
                  const sRate = parseFloat(batchSellingRate) || 0;
                  const cRate = parseFloat(batchCostRate) || 0;
                  const sPrice = Math.round((amt * sRate) / 100);
                  const cPrice = Math.round((amt * cRate) / 100);
                  const margin = sPrice - cPrice;
                  return (
                    <div key={amt} className="flex justify-between items-center text-[11px] py-0.5 border-b border-plum-border/40 last:border-b-0">
                      <span className="font-mono text-plum-muted">{amt.toLocaleString()} Ks</span>
                      <div className="flex items-center space-x-2 font-mono">
                        <span className="font-bold text-primary">{sPrice.toLocaleString()} Ks ရောင်း</span>
                        <span className="text-[10px] text-status-green">+{margin} အမြတ်</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowBatchModal(false)}
                  className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
                >
                  ပယ်ဖျက်မည်
                </button>
                <button
                  type="submit"
                  disabled={batchSaving}
                  className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
                >
                  {batchSaving ? 'ပြောင်းလဲနေပါသည်...' : 'အတည်ပြုပြောင်းမည်'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Buyback Rates Modal */}
      {isEditingRates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-plum-border/60">
              <h3 className="font-bold text-plum-dark text-base flex items-center space-x-1.5">
                <Sliders className="w-4 h-4 text-primary" />
                <span>ဖုန်းဘေလ်ပြန်ဝယ်နှုန်း (%) ပြင်ရန်</span>
              </h3>
              <button
                type="button"
                onClick={() => setIsEditingRates(false)}
                className="p-1 rounded-full text-plum-muted hover:text-plum-dark"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-plum-muted leading-relaxed">
              Customer များထံမှ ဖုန်းဘေလ်ပြန်လည်ဝယ်ယူရာတွင် အသုံးပြုမည့် ရာခိုင်နှုန်း (%) ကို ပြင်ဆင်သတ်မှတ်ပါ:
            </p>

            <form onSubmit={handleSaveAllBuybackRates} className="space-y-3">
              {(['mpt', 'atom', 'mytel', 'u9'] as OperatorId[]).map((op) => (
                <div key={op} className="flex items-center justify-between bg-surface-page p-2.5 rounded-xl border border-plum-border">
                  <div className="flex items-center space-x-2">
                    <OperatorLogo id={op} size="xs" className="rounded-md" />
                    <span className="text-xs font-bold text-plum-dark uppercase">{OPERATOR_CONFIGS[op].name}</span>
                  </div>
                  <div className="flex items-center space-x-1.5 w-24">
                    <input
                      type="number"
                      step="0.1"
                      min="10"
                      max="100"
                      value={rateInputs[op]}
                      onChange={(e) =>
                        setRateInputs((prev) => ({ ...prev, [op]: e.target.value }))
                      }
                      className="w-full bg-surface-card border border-plum-border rounded-lg px-2 py-1 text-center font-mono font-bold text-xs text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <span className="text-xs font-bold text-plum-muted">%</span>
                  </div>
                </div>
              ))}

              <div className="flex space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsEditingRates(false)}
                  className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
                >
                  ပယ်ဖျက်မည်
                </button>
                <button
                  type="submit"
                  disabled={rateSaving}
                  className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
                >
                  {rateSaving ? 'သိမ်းဆည်းနေပါသည်...' : 'အတည်ပြုသိမ်းမည်'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Settle Modal with Tx Ref */}
      {settlingId && activeSettlingBuyback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4">
            <h3 className="font-bold text-plum-dark text-base">ငွေထုတ်ပေးပြီးကြောင်း အတည်ပြုရန်</h3>
            <p className="text-xs text-plum-muted font-mono">ဘေလ်ပြန်ဝယ်မှု ID: {settlingId}</p>

            {/* Payout Info Notice */}
            <div className="p-3 bg-surface-page rounded-xl border border-plum-border/60 text-xs space-y-1.5">
              <div className="flex justify-between">
                <span className="text-plum-muted">ငွေလွှဲပေးရမည့်ပမာဏ:</span>
                <span className="font-bold font-mono text-primary text-sm">
                  {activeSettlingBuyback.payout_amount.toLocaleString()} Ks
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-plum-muted">ထုတ်ပေးမည့်နေရာ:</span>
                <span className="font-bold text-plum-dark">
                  {activeSettlingBuyback.settlement_type === 'wallet'
                    ? 'PluvP Wallet (အလိုအလျောက် ပေါင်းထည့်မည်)'
                    : activeSettlingBuyback.settlement_type === 'external_wave'
                    ? `WavePay (${activeSettlingBuyback.payout_destination})`
                    : `KBZPay (${activeSettlingBuyback.payout_destination})`}
                </span>
              </div>
            </div>

            {activeSettlingBuyback.settlement_type !== 'wallet' && (
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">
                  ငွေလွှဲပြေစာ အမှတ် (Transaction Reference)
                </label>
                <input
                  type="text"
                  value={payoutTxRef}
                  onChange={(e) => setPayoutTxRef(e.target.value)}
                  placeholder={
                    activeSettlingBuyback.settlement_type === 'external_wave'
                      ? 'ဥပမာ: WP-OUT-991823'
                      : 'ဥပမာ: KP-OUT-991823'
                  }
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs font-mono text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            )}

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setSettlingId(null)}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
              >
                ပယ်ဖျက်မည်
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleConfirmSettle}
                className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
              >
                {actionLoading ? 'အတည်ပြုနေပါသည်...' : 'အတည်ပြုပြီး အပြီးသတ်မည်'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
